import React, { useEffect, useState } from 'react';
import {
   Box,
   Button,
   Dialog,
   DialogActions,
   DialogContent,
   DialogTitle,
   List,
   ListItem,
   ListItemIcon,
   ListItemText,
   Typography,
} from '@mui/material';
import { withFirebase } from '../Firebase';
import { useAuth } from '../../providers';
import { spaceTypeSVG } from '../Spaces/functions';

const ChipDialog = (props) => {
   const [ignoreAvailable, setIgnoreAvailable] = useState(false);
   const [eligibleBookings, setEligibleBookings] = useState(null);
   const [ineligibleBookings, setIneligibleBookings] = useState(null);

   useEffect(() => {
      if (
         props.chipModal.open &&
         props.data.spaces &&
         props.data.space &&
         (props.data.space?.bookingNo > 0 || Object.keys(props.data.space).length > 0)
      ) {
         let ignore = false;
         let eligible = [];
         let ineligible = [];
         Object.entries(props.data.space).map(([key, value]) => {
            if (key !== 'bookingNo' && props.data.spaces[value.sid]) {
               let booking = {
                  bookingNo: key,
                  sid: value.sid,
                  name: props.data.spaces[value.sid].name,
                  seat: value.seat,
                  hourly: false,
                  type: props.data.spaces[value.sid].type,
                  title: null,
                  backgroundColor: props.data.spaces[value.sid].backgroundColor || null,
               };
               if (value.start) {
                  booking.hourly = true;
                  booking.start = value.start;
                  booking.end = value.end;
                  booking.title = value.title;
               }
               if (props.data.spaces[value.sid].ignoreWhenChangingStatus === true) {
                  ignore = true;
                  eligible.push(booking);
               } else {
                  ineligible.push(booking);
               }
            }
         });
         setEligibleBookings(eligible);
         setIneligibleBookings(ineligible);
         setIgnoreAvailable(ignore);
      }
   }, [props.data.spaces, props.data.space, props.chipModal.open]);

   const { authUser } = useAuth();
   const handleClose = () => {
      props.setChipModal({ open: false });
   };

   const handleRemove = async (keep = false) => {
      var type = false;
      if (props.chipModal.type == 'split') {
         type = 'split';
         await props.firebase.cancelBookedSpaceForUser(props.data, authUser, keep, type);
      } else if (props.chipModal.type == 'wrongLocation') {
         props.setChipModal({ open: false });
         try {
            await props.firebase.resetDayForUser(props.data);
         } catch (error) {
            console.warn(error, 'Error resetting day');
         }
      } else if (props.chipModal.type == 'merge') {
         type = 'merge';
         await props.firebase.cancelBookedSpaceForUser(props.data, authUser, keep, type);
      } else if (props.chipModal.type == 'AM' || props.chipModal.type == 'PM') {
         type = props.chipModal.type;
         await props.firebase.cancelBookedSpaceForUser(props.data, authUser, keep, type);
      } else if (props.data.space !== 'empty') {
         try {
            await props.firebase.cancelBookedSpaceForUser(
               props.data,
               authUser,
               keep,
               type,
            );
         } catch (error) {
            console.warn('Error removing space booking', error);
         }
         if (props.data.offsiteLocation !== null) {
            try {
               await props.firebase.removeOffSiteLocation(props.data);
            } catch (error) {
               console.warn(
                  'Error removing offsite location after removing bookings',
                  error,
               );
            }
         }
      } else if (props.data.offsiteLocation !== null) {
         await props.firebase.removeOffSiteLocation(props.data);
      }
      props.setChipModal({ open: false });
   };

   const ListBookings = ({ bookings }) => {
      function processBookingName(booking) {
         let name = '';
         if (booking.title !== null) {
            name = `${booking.title} - ${booking.name}`;
         } else {
            name = booking.name;
         }
         return name;
      }
      return (
         <List dense={true}>
            {bookings.map((booking) => (
               <ListItem
                  sx={{
                     borderLeft:
                        booking.backgroundColor !== null
                           ? `4px solid ${booking.backgroundColor}`
                           : 'none',
                  }}
               >
                  <ListItemIcon>{spaceTypeSVG(booking.type)}</ListItemIcon>
                  <ListItemText primary={processBookingName(booking)} />
               </ListItem>
            ))}
         </List>
      );
   };

   const OffsiteBody = () => {
      return (
         <React.Fragment>
            <Typography variant="body1" gutterBottom>
               Before you can change this status you need to remove the location you
               previously named and any bookings you have made.
            </Typography>
            <Typography variant="body1">
               To continue, click on Remove and then change status.
            </Typography>
         </React.Fragment>
      );
   };
   const SpacesBody = () => {
      return (
         <React.Fragment>
            <Typography variant="body1" gutterBottom>
               You have {props.data.space.bookingNo} booking
               {props.data.space.bookingNo > 1 && 's'} on{' '}
               {`${props.data.date.shortDayName}, ${props.data.date.day} ${props.data.date.shortMonthName}`}
               .
            </Typography>
            {ineligibleBookings?.length > 0 && (
               <Box>
                  <ListBookings bookings={ineligibleBookings} />

                  <Typography variant="body1" gutterBottom>
                     {ineligibleBookings.length > 1 ? 'These' : 'This'}{' '}
                     {ineligibleBookings.length > 1 && `${ineligibleBookings.length} `}
                     {ineligibleBookings.length > 1 ? 'bookings' : 'booking'} will need to
                     be removed before you can change your status.
                  </Typography>
               </Box>
            )}

            {ignoreAvailable && (
               <Box>
                  <Typography variant="body1" gutterBottom>
                     If still needed, {eligibleBookings.length > 1 ? 'these' : 'this'}{' '}
                     {eligibleBookings.length > 1 ? 'bookings' : 'booking'} can be kept
                     even if you are not in the office.
                  </Typography>
                  <ListBookings bookings={eligibleBookings} />
                  <Typography variant="caption" gutterBottom>
                     Kept bookings can only be manually removed at a later date.
                  </Typography>
               </Box>
            )}
         </React.Fragment>
      );
   };
   const WrongLocationBody = () => {
      return (
         <React.Fragment>
            <Typography variant="body1" gutterBottom>
               You set this status when you had a different primary location, which can
               not be updated.
            </Typography>
            <Typography variant="body1" gutterBottom>
               You will need to reset the status before any changes can be made. This will
               remove all bookings and offsite locations for this day.
            </Typography>
            <Typography variant="body1">
               To continue, click on Remove and then set your statuses again.
            </Typography>
         </React.Fragment>
      );
   };
   const SplitBody = () => {
      return (
         <React.Fragment>
            <Typography variant="body1" gutterBottom>
               Before you can split this status you need to remove any 'All day' and 'PM'
               space bookings.
            </Typography>
            <Typography variant="body1" gutterBottom>
               Once removed the spaces will be available for others to book.
            </Typography>
            <Typography variant="body1">
               To continue, click on Remove and then click on Split Day status.
            </Typography>
         </React.Fragment>
      );
   };
   const MergeBody = () => {
      return (
         <React.Fragment>
            <Typography variant="body1" gutterBottom>
               Before you can merge your statuses you need to remove all 'PM' space
               bookings for this day.
            </Typography>
            <Typography variant="body1" gutterBottom>
               Once removed the spaces will be available for others to book.
            </Typography>
            <Typography variant="body1">
               To continue, click on Remove and then click on Split Day to toggle it off.
            </Typography>
         </React.Fragment>
      );
   };
   const AMPMBody = () => {
      return (
         <React.Fragment>
            <Typography variant="body1" gutterBottom>
               Before you can change your {props.chipModal.type} status, you need to
               remove all {props.chipModal.type} space bookings.
            </Typography>
            <Typography variant="body1" gutterBottom>
               Once removed the spaces will be available for others to book.
            </Typography>
            <Typography variant="body1">
               To continue, click on Remove and then change your {props.chipModal.type}{' '}
               status.
            </Typography>
         </React.Fragment>
      );
   };

   return (
      <div>
         <Dialog
            open={props.chipModal.open}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
         >
            <DialogTitle id="alert-dialog-title">
               {props.chipModal.type == 'split'
                  ? `Split your status?`
                  : props.chipModal.type == 'wrongLocation'
                  ? `Remove day details`
                  : props.chipModal.type == 'merge'
                  ? `Merge your statuses?`
                  : `Remove your bookings?`}
            </DialogTitle>
            <DialogContent>
               {props.chipModal.type == 'wrongLocation' ? (
                  <WrongLocationBody />
               ) : props.chipModal.type == 'split' ? (
                  <SplitBody />
               ) : props.chipModal.type == 'merge' ? (
                  <MergeBody />
               ) : props.chipModal.type == 'AM' || props.chipModal.type == 'PM' ? (
                  <AMPMBody />
               ) : props.data.offsiteLocation !== null ? (
                  <OffsiteBody />
               ) : (
                  <SpacesBody />
               )}
            </DialogContent>
            <DialogActions>
               <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
               >
                  <Button onClick={handleClose} color="primary">
                     Cancel
                  </Button>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                     {ignoreAvailable && props.chipModal.type === undefined && (
                        <Button onClick={() => handleRemove(true)} variant="outlined">
                           Keep {` ${eligibleBookings.length}`}
                        </Button>
                     )}
                     <Button
                        onClick={() => handleRemove(false)}
                        color="secondary"
                        variant="contained"
                     >
                        Remove{' '}
                        {ignoreAvailable && props.chipModal.type === undefined && ` all`}
                     </Button>
                  </Box>
               </Box>
            </DialogActions>
         </Dialog>
      </div>
   );
};
export default withFirebase(ChipDialog);
